import { type SecondaryCategory } from '@webapp/core/entities/internal/SecondaryCategory'
import { type ApiResponse } from '@webapp/core/interfaces/api/ApiResponse'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IPermissionRepository } from '@webapp/platform/iam/business-logic/interfaces/IPermissionRepository'
import { ObjectPermissions } from '@webapp/platform/iam/entities/enums/ObjectPermissionEnum'
import { type PermissionConstructor } from '@webapp/platform/iam/entities/Permission'
import { type ReportAccessGrantConstructor } from '@webapp/platform/iam/entities/ReportAccessGrant'
import { type PermissionApiModel } from '../../api-models/PermissionApiModel'
import { type ReportAccessGrantApiModel } from '../../api-models/ReportAccessGrantApiModel'
import { mapPermissionApiModel } from '../../mapper/mapPermissionApiModel'
import { mapReportAccessGrantApiModel } from '../../mapper/mapReportAccessGrantApiModel'
import {
  type AssignPermissionOptions,
  type AssignUnassignFilterGroup,
} from '../../types/assignPermissionPayload'

interface Dependency {
  httpClient: IHttpClient
}

export class PermissionRepository implements IPermissionRepository {
  constructor(private readonly deps: Dependency) {}

  async getSystemPermissions(): Promise<
    RepoStandardResponse<PermissionConstructor[]>
  > {
    const res = await this.deps.httpClient.get<
      ApiResponse<PermissionApiModel[]>
    >(`/api/v3/permissions/`, {
      params: {
        page_size: 'all',
      },
    })

    const permissions = res.data.data

    return {
      data: permissions.map((permission) => mapPermissionApiModel(permission)),
    }
  }

  async assignToPrimaryCategories(
    permissionId: number,
    userAuthId: number,
    primaryCategoryContentTypeId: number,
    primaryCategoryIds: number[],
    { all = false }: AssignPermissionOptions = {}
  ): Promise<void> {
    try {
      await this.deps.httpClient.put<
        RepoStandardResponse<number[]>,
        AssignUnassignFilterGroup[]
      >(`/api/v3/permissions/filters/assign-unassign/`, [
        {
          all,
          filters: primaryCategoryIds.map((primaryCategoryId) => ({
            object_id: primaryCategoryId,
            content_type: primaryCategoryContentTypeId,
            user: userAuthId,
            permission: permissionId,
          })),
          permission: permissionId,
          user: userAuthId,
          content_type: all ? primaryCategoryContentTypeId : undefined,
          editing_scope: 'location',
        },
      ])
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async assignToSecondaryCategories(
    permissionId: number,
    userAuthId: number,
    primaryCategoryContentTypeId: number,
    primaryCategoryIds: number[],
    secondaryCategoryContentTypeId: number,
    secondaryCategories: SecondaryCategory[],
    { all = false }: AssignPermissionOptions = {}
  ): Promise<void> {
    try {
      const secondaryCategoryIds = secondaryCategories.map((s) => s.id)

      await this.deps.httpClient.put<
        RepoStandardResponse<number[]>,
        AssignUnassignFilterGroup[]
      >(`/api/v3/permissions/filters/assign-unassign/`, [
        {
          all,
          filters: [
            ...secondaryCategoryIds.map((secondaryCategoryId) => ({
              object_id: secondaryCategoryId,
              content_type: secondaryCategoryContentTypeId,
              user: userAuthId,
              permission: permissionId,
            })),
            ...primaryCategoryIds.map((primaryCategoryId) => ({
              object_id: primaryCategoryId,
              content_type: primaryCategoryContentTypeId,
              user: userAuthId,
              permission: permissionId,
            })),
          ],
          permission: permissionId,
          user: userAuthId,
          content_type: all ? secondaryCategoryContentTypeId : undefined,
          editing_scope: 'department',
        },
      ])
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async listReportAccessGrants(params: {
    secondaryCategoryContentTypeId: number
    secondaryCategoryId: number
  }): Promise<ReportAccessGrantConstructor[]> {
    try {
      const res = await this.deps.httpClient.get<
        ApiResponse<ReportAccessGrantApiModel[]>
      >(`/api/v3/permissions/objects/`, {
        params: {
          content_type: params.secondaryCategoryContentTypeId,
          object_pk: params.secondaryCategoryId,
          order_by: 'user.lastName',
          permission: ObjectPermissions.VIEW_REPORT,
        },
      })

      return res.data.data.map((grant) => mapReportAccessGrantApiModel(grant))
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async grantReportAccess(payload: {
    userAuthId: number
    secondaryCategoryContentTypeId: number
    secondaryCategoryId: number
  }): Promise<ReportAccessGrantConstructor> {
    try {
      const res = await this.deps.httpClient.post<
        ApiResponse<ReportAccessGrantApiModel>,
        {
          user: number
          content_type: number
          permission: string
          object: number
        }
      >(`/api/v3/permissions/objects/`, {
        user: payload.userAuthId,
        content_type: payload.secondaryCategoryContentTypeId,
        permission: ObjectPermissions.VIEW_REPORT,
        object: payload.secondaryCategoryId,
      })

      return mapReportAccessGrantApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async revokeReportAccessGrant(grantId: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(
        `/api/v3/permissions/objects/${grantId}/`
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async clearSecondaryCategoryReportAccess(payload: {
    secondaryCategoryContentTypeId: number
    secondaryCategoryId: number
  }): Promise<void> {
    try {
      await this.deps.httpClient.delete(`/api/v3/permissions/objects/object/`, {
        data: {
          content_type: payload.secondaryCategoryContentTypeId,
          permission: ObjectPermissions.VIEW_REPORT,
          object: payload.secondaryCategoryId,
        },
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async grantReportAccessToAllSecondaryCategories(payload: {
    primaryCategoryId: number
    userAuthId: number
  }): Promise<void> {
    try {
      await this.deps.httpClient.post<void, { location: number; user: number }>(
        `/api/v3/reports/permissions/`,
        {
          location: payload.primaryCategoryId,
          user: payload.userAuthId,
        }
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async grantReportAccessToAllPrimaryCategories(payload: {
    userAuthId: number
    secondaryCategoryContentTypeId: number
  }): Promise<void> {
    try {
      await this.deps.httpClient.post<
        void,
        { user: number; content_type: number; permission: string }
      >(`/api/v3/permissions/objects/user/`, {
        user: payload.userAuthId,
        content_type: payload.secondaryCategoryContentTypeId,
        permission: ObjectPermissions.VIEW_REPORT,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async revokeReportAccessFromAllPrimaryCategories(payload: {
    userAuthId: number
    secondaryCategoryContentTypeId: number
  }): Promise<void> {
    try {
      await this.deps.httpClient.delete(`/api/v3/permissions/objects/user/`, {
        data: {
          user: payload.userAuthId,
          content_type: payload.secondaryCategoryContentTypeId,
          permission: ObjectPermissions.VIEW_REPORT,
        },
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
